import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useState } from "react";
import { matchesUrlPattern } from "../../../../shared/connectors/matchesUrlPattern";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useConnectorCreator } from "../../../hooks/useConnectorCreator";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { CodeBlock } from "../../CodeBlock";
import { ConnectorSelect } from "../../ConnectorSelect";
import { Icon } from "../../Icon";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import { SegmentedControl } from "../../SegmentedControl";
import {
  HeaderRight,
  SettingsModalBody,
  SettingsModalFooter,
  SettingsModalHeader,
} from "../../SettingsLayout";
import { Text } from "../../Text";
import { useToast } from "../../Toast";
import {
  Card,
  FieldLabel,
  NeutralIconBadge,
  settingsModalStyle,
} from "../shared";
import { DestinationStatusPill } from "./DestinationStatusPill";

const UrlRow = styled("div")`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DisabledOpener = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 32px;
  padding: 0 8px 0 12px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  background: var(--alpha4);
  color: var(--text-secondary);
`;

type Draft = {
  id: string;
  name: string;
  url: string;
  method: "PUT" | "POST";
  connectedAppId: string | null;
};

type Props = {
  open: boolean;
  destinationId: string | null;
  onClose: () => void;
};

export function AuditLogDestinationModal({
  open,
  destinationId,
  onClose,
}: Props) {
  const z = useZero();
  const { showToast } = useToast();
  const [destinations] = useQuery(queries.auditLogDestinations());
  const [allApps] = useQuery(queries.connectorsIndex());

  // Seeded once at mount (both call sites mount the modal open and unmount it
  // on close): re-seeding when destination sync delivers changes would
  // clobber in-progress edits.
  const [draft, setDraft] = useState<Draft>(() => {
    const initial = destinationId
      ? destinations.find((d) => d.id === destinationId)
      : null;
    return initial
      ? {
          id: initial.id,
          name: initial.name,
          url: initial.url,
          method: initial.method as "PUT" | "POST",
          connectedAppId: initial.appUsage?.connectedAppId ?? null,
        }
      : {
          id: nanoid(),
          name: "",
          url: "",
          method: "PUT",
          connectedAppId: null,
        };
  });
  const createConnector = useConnectorCreator((id) =>
    setDraft((current) => ({ ...current, connectedAppId: id }))
  );

  const [hasFocused, setHasFocused] = useState(false);
  const nameRef = useCallback(
    (node: HTMLInputElement | null) => {
      if (!node) return;
      if (destinationId !== null) return;
      if (hasFocused) return;
      setHasFocused(true);
      node.focus();
    },
    [destinationId, hasFocused]
  );

  const persisted = destinations.find((d) => d.id === draft.id) ?? null;

  // Fall back to the destination's joined connector when the bound app
  // isn't in the user's RBAC-readable list, so the opener stays legible.
  const persistedApp = persisted?.appUsage?.connectedApp ?? null;
  const selectedApp = draft.connectedAppId
    ? (allApps.find((a) => a.id === draft.connectedAppId) ??
      (persistedApp?.id === draft.connectedAppId ? persistedApp : null))
    : null;

  let isValidUrl = false;
  try {
    isValidUrl = new URL(draft.url).protocol === "https:";
  } catch {
    isValidUrl = false;
  }
  const candidateApps = isValidUrl
    ? allApps.filter((a) => matchesUrlPattern(a.urlPattern, draft.url))
    : [];

  const fieldsValid =
    draft.name.trim().length > 0 && draft.url.trim().startsWith("https://");

  const hasChanges =
    !persisted ||
    draft.name.trim() !== persisted.name ||
    draft.url.trim() !== persisted.url ||
    draft.method !== persisted.method ||
    draft.connectedAppId !== (persisted.appUsage?.connectedAppId ?? null);

  const canSave = fieldsValid && hasChanges;

  const save = () => {
    if (!canSave) return;
    const args = {
      id: draft.id,
      name: draft.name.trim(),
      url: draft.url.trim(),
      method: draft.method,
      connectedAppId: draft.connectedAppId,
    };
    if (persisted) {
      z.mutate(sharedMutators.updateAuditLogDestination(args));
    } else {
      z.mutate(sharedMutators.createAuditLogDestination(args));
      showToast({
        icon: "check",
        message: `Destination “${args.name}” created`,
        buttonLabel: "OK",
      });
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} style={settingsModalStyle}>
      <SettingsModalHeader>
        <NeutralIconBadge iconName="output" />
        <Text variant="text-regular-md">
          {persisted
            ? draft.name || "Audit log destination"
            : "New destination"}
        </Text>
        <HeaderRight>
          <Button size="l" variant="secondary" icon="x" onClick={onClose} />
        </HeaderRight>
      </SettingsModalHeader>

      <SettingsModalBody style={{ gap: 16 }} data-popover-host>
        <Card>
          <FieldLabel>
            <Box horizontal={8} vertical={10}>
              <Text variant="text-small-md">Name</Text>
            </Box>
            <Input
              ref={nameRef}
              placeholder="e.g. Compliance archive"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              style={{ width: "100%" }}
            />
          </FieldLabel>

          <FieldLabel>
            <Box horizontal={8} vertical={10}>
              <Text variant="text-small-md">URL</Text>
            </Box>
            <UrlRow>
              <SegmentedControl
                options={["PUT", "POST"] as const}
                value={draft.method}
                onChange={(method) => setDraft({ ...draft, method })}
              />
              <Input
                placeholder="https://my-bucket.s3.us-east-1.amazonaws.com/audit-logs/{filename}"
                value={draft.url}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                style={{ flex: 1, minWidth: 0 }}
              />
            </UrlRow>
            <Box horizontal={8} top={6}>
              <Text variant="text-small" color="var(--text-secondary)">
                Use{" "}
                <code style={{ fontFamily: "monospace" }}>{`{filename}`}</code>{" "}
                to insert a per-batch JSONL filename (needed for object stores
                like S3; omit for webhooks).
              </Text>
            </Box>
          </FieldLabel>

          <FieldLabel>
            <Box horizontal={8} vertical={10}>
              <Text variant="text-small-md">Auth</Text>
            </Box>
            {isValidUrl ? (
              <ConnectorSelect
                apps={candidateApps}
                value={draft.connectedAppId}
                selectedName={selectedApp?.name ?? null}
                onChange={(id) => setDraft({ ...draft, connectedAppId: id })}
                placeholder="No connector selected"
                addNew={[
                  {
                    label: "New API key",
                    onClick: () => void createConnector("apiKey"),
                  },
                ]}
              />
            ) : (
              <DisabledOpener>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Enter a URL first
                </Text>
                <span style={{ opacity: 0.3 }}>
                  <Icon name="chevron-down" size={12} />
                </span>
              </DisabledOpener>
            )}
            <Box horizontal={8} top={6}>
              <Text variant="text-small" color="var(--text-secondary)">
                The proxy will inject this connector’s credentials (e.g. AWS
                SigV4, API key) into outgoing requests. Leave blank for public
                endpoints.
              </Text>
            </Box>
          </FieldLabel>
        </Card>
        {persisted?.lastError && (
          <Card>
            <FieldLabel>
              <Box horizontal={8} bottom={10}>
                <Text variant="text-small-md">Error message</Text>
              </Box>
              <CodeBlock wrap content={persisted.lastError} />
            </FieldLabel>
          </Card>
        )}
      </SettingsModalBody>

      <SettingsModalFooter style={{ gap: 8, alignItems: "center" }}>
        {persisted && <DestinationStatusPill destination={persisted} />}
        <Button size="l" onClick={save} disabled={!canSave}>
          Save
        </Button>
      </SettingsModalFooter>
    </Modal>
  );
}
